const markdown = `*Solidigm 与合作伙伴 NewFW Technology（芯风威）联手，在 MLPerf Storage v2.0 中验证旗舰企业级 SSD 在机器学习与大模型训练负载下的性能。*

## 引言：AI 浪潮之巅，Solidigm SSD 性能再获权威认证

随着大语言模型（LLM）等前沿技术的飞速发展，人工智能（AI）正以前所未有的深度和广度重塑各行各业。这场技术革命的背后，是对数据和算力的海量需求，这也将基础架构中的每一个环节都推向了性能的极限。其中，存储系统作为数据流动的核心枢纽，其性能直接决定了昂贵的 GPU 算力能否被充分利用，已成为构建高效 AI 平台的关键一环。

在此背景下，我们荣幸地宣布，Solidigm 的两款旗舰级企业级 SSD——性能猛兽 Solidigm D7-PS1030 与容量巨擘 Solidigm D5-P5336，在业界公认的权威 AI 存储基准测试 MLPerf® Storage v2.0 中，取得了卓越的性能成绩。在此次关键测试中，Solidigm 与合作伙伴 NewFW Technology 联手，充分验证了 Solidigm SSD 在机器学习（ML）和大模型（LLM）训练工作负载下的硬实力。

## 1. 为何 MLPerf® Storage 是 AI 存储的“试金石”？

MLPerf® Storage 并非普通的性能跑分工具，而是专为评估 AI 训练负载下存储性能而设计的行业黄金标准。

- **权威性：** MLPerf® 由 MLCommons® 存储工作组发起，由十多家行业领先组织合作创建，是全球首个专门衡量机器学习（ML）工作负载下存储性能的基准测试套件。它的诞生旨在为行业提供一个公平、可复现的性能衡量标尺。
- **核心价值：** 其根本目标是帮助客户在存储和计算资源之间找到最佳平衡点。通过精准评估存储性能，MLPerf Storage 有效解决了因 I/O 性能不足而导致的“GPU 饥饿”（GPU Starvation）问题，确保每一分算力投资都物有所值。
- **独特的工作原理：** MLPerf Storage 的独到之处在于，它采用了一种新颖的模拟机制，能够在不实际动用昂贵加速器（如 GPU）的情况下，精确捕捉并复现神经网络训练过程中的真实 I/O 模式。这意味着它可以独立、准确地评估存储系统本身的性能，而不受计算环节的干扰。

![图 1：MLPerf Storage 的高保真模拟。在不使用 GPU 的情况下，MLPerf Storage 精确复现了真实 AI 训练中的磁盘 I/O 和内存访问模式，确保了测试的有效性与独立性。](/media/blog/mlperf-storage-v2/fig-01.jpg)

## 2. 本次测试的主角：Solidigm 性能猛兽与容量巨擘

本次测试中，Solidigm 派出了两款定位鲜明、性能卓越的明星产品。

### 2.1 性能猛兽：Solidigm D7-PS1030

Solidigm D7-PS1030 是一款专为满足顶级 GPU（如 NVIDIA H100）集群海量数据吞吐需求而生的高性能企业级 SSD。它旨在消除 I/O 瓶颈，让 AI 训练畅通无阻。

- **接口：** PCIe 5.0 x4, NVMe
- **顺序读取速度：** 高达 14,500 MB/s
- **顺序写入速度：** 高达 10,000 MB/s

### 2.2 容量巨擘：Solidigm D5-P5336

作为“全球最高容量的 PCIe SSD”，Solidigm D5-P5336 旨在通过极致的存储密度，帮助客户在 AI 和数据密集型工作负载中，显著提升基础设施效率和可持续性，是应对海量非结构化数据的理想选择。

- **接口：** PCIe 4.0 x4, NVMe
- **容量：** 本次测试中使用 61.44TB 版本
- **技术：** 采用先进的 QLC NAND 技术，专为现代读取密集型工作负载（如数据湖、对象存储）进行了深度优化。

## 3. 通往巅峰之路：专业工具与深度调优

顶级硬件的潜力释放，离不开专业的测试工具和系统级的深度优化。在本次测试中，我们与 NewFW Technology 团队紧密合作，共同解决了一系列挑战，展现了从发现问题到解决问题的完整技术路径。

### 3.1 测试环境准备

测试在单存储节点上进行：一台 2U 双路 CPU 服务器，由 NewFW Technology 的 CWP 工作负载平台下发基准测试 Recipe。

![图 3：单存储节点，使用 2U 双路 CPU 服务器，通过 NewFW Technology CWP 工作负载平台下发基准测试 Recipe](/media/blog/mlperf-storage-v2/fig-03.jpg)

**基础硬件环境。** 在本次 MLPerf® Storage v2.0 基准测试中，我们构建了一个高性能、工业级的单节点存储测试环境，旨在通过严苛的硬件配置与先进的软件栈，真实还原 AI 数据中心在处理大规模深度学习负载时的运行状态。

**软件与管理平台。** 使用了 NewFW Technology 开发的 CWP（Cloud Workload Platform）云工作负载平台。该平台作为环境的“指挥中心”，负责自动化的任务部署（Recipes 管理）、实时指标采集（涵盖 CPU、内存、NVMe 硬盘 SMART 信息及模拟 GPU 状态）以及故障检测。

**负载模拟规范。** 测试环境严格遵循 MLPerf® 的权威标准，以模拟真实的 AI 训练流水线：

- **模拟加速器：** 精准模拟了 NVIDIA H100 和 A100 在执行训练任务时的 I/O 行为。
- **测试模型：** 涵盖了 3D U-Net（医疗影像分割）、ResNet-50（图像分类）以及 CosmoFlow 等典型负载。
- **数据规模约束：** 依据 MLPerf 规范，数据集规模被设定为系统内存的 5 倍以上，以排除系统缓存（Page Cache）对测试结果的干扰，确保测试的是存储设备的真实吞吐能力。

通过 NewFW Technology CWP 平台，我们在这个环境下实现了对 I/O 路径的深度洞察，从而能够针对内存压力竞态、缓存抖动等瓶颈进行精准调优，最终解锁了 Solidigm SSD 的巅峰性能。

### 3.2 初始挑战：“性能缩水”之谜

作为架构师，我们的第一步是进行基准性能测试。然而，初始结果立刻引起了我们团队的警觉。尽管 D7-PS1030 的标称读取速度高达 14.5 GB/s，但在我们的初始测试中，仅获得了 11.9 GB/s 的成绩。这个数值虽与社区测试平均值（11.45 GB/s）相近，但远未达到硬件应有的极限，表明系统中存在着隐藏的性能瓶颈。

![图 4：初始性能差距分析。尽管 D7-PS1030 标称性能高达 14.5 GB/s，但初始测试仅为 11.9 GB/s。](/media/blog/mlperf-storage-v2/fig-04.jpg)

### 3.3 利其器：NewFW Technology CWP 平台助力深度洞察

面对多模型、多参数、多硬件组合带来的极高测试复杂度，要精准定位瓶颈，我们需要强大的诊断工具。为此，我们采用了合作伙伴 NewFW Technology 的云工作负载平台（Cloud Workload Platform, CWP）。

CWP 是一个集工作负载管理、全过程多维度实时监控、自动化测试和性能分析于一体的强大平台。它让我们能够清晰地观察到测试过程中的 CPU 负载、内存压力、磁盘 I/O 曲线等数十项关键指标。

![图 5：NewFW Technology CWP 平台提供了从工作负载编排到深度监控的端到端解决方案](/media/blog/mlperf-storage-v2/fig-05.jpg)

![图 6：NewFW Technology CWP 工作负载系统截图，任务列表与任务过程监控](/media/blog/mlperf-storage-v2/fig-06.jpg)

借助 CWP 的实时监控能力，我们迅速找到了问题的“罪证”。如下图所示，在 384GB 内存配置下，磁盘的读写性能曲线（黄线）出现了剧烈的抖动和显著的性能下降。这一 I/O 波动与 CWP 监控到的高内存压力指标完全吻合。这便是我们的“确凿证据”：性能瓶颈并非 SSD 本身，而是由系统内存不足引发的“缓存抖动”（Cache Thrashing）和激烈的系统资源竞争。

![图 7：内存总量对于测试结果的影响。内存有限时容易出现 GPU Starvation，尤其是第二轮基准测试性能抖动和下降明显。](/media/blog/mlperf-storage-v2/fig-07.jpg)

![图 8：CWP 捕获的证据。在 384GB 内存配置下，磁盘吞吐量出现剧烈抖动并持续下降，明确指向系统内存瓶颈，而非 SSD 硬件本身。](/media/blog/mlperf-storage-v2/fig-08.jpg)

## 4. 释放全部潜能：调优后的卓越成果

基于 CWP 平台的精准诊断，我们的假设得到了证实，这指导我们制定了一套综合的系统级调优策略，并最终成功释放了 D7-PS1030 的全部潜能。

核心优化措施包括（不仅限于）：

- **工作负载参数调优：** 我们首先调整了 MLPerf 的 \`reader_threads\` 参数，从 16 降至 10。从架构角度看，最初 16 个读取线程就像“过度热情的生产者”，从 SSD 读取数据的速度远超“消费者”（模拟的训练进程）的处理速度，导致数据在内存中严重拥塞。通过减少线程数，我们完美同步了数据流，消除了资源争抢。
- **操作系统（OS）调优：** 我们禁用了内核大页内存（Huge Pages），以避免其在内存紧张时因碎片整理带来额外的系统开销。此外，针对 unet3d 模型的 I/O 特点，我们发现 ext4 文件系统更简单的元数据结构能减少内核级内存管理开销（slab 抖动），这在内存受限的场景下至关重要。当然 ext4 也不是所有模型都适用，在其他模型（cosmoflow 和 unet3d）的测试中，XFS 也表现出色。
- **NUMA 亲和性优化：** 我们开启了内核的 \`numa_balancing\` 功能，确保测试进程、其使用的内存以及 D7-PS1030 设备被调度在同一个 NUMA 节点上。这一措施有效避免了跨节点内存访问带来的延迟，最大化了数据通路效率。

## 5. 最终成果：横扫社区公开成绩

经过 CWP 平台的深度“治疗”，我们的 SSD 终于爆发出了真正的实力。

在 3D U-Net（模拟 H100/A100）负载中，Solidigm 的成绩令人瞩目：

- **D7-PS1030：** 最终测得峰值顺序读带宽达 **14,218 MB/s**，AU 高达 **94.07%**。这一成绩远超业界其他厂家 11–12 GB/s 的平均水平。横向对比结果显示：在同等配置下，Solidigm 的吞吐量和 GPU 利用率均位列行业第一梯队，显著优于 Micron、Samsung、Kioxia 等竞品。
- **D5-P5336：** 同样表现不俗，读取速度峰值达 **7,138 MB/s**，unet3d 中位数 **7,129.83 MB/s**，在 AU > 90% 前提下稳定跑完 5 轮，超越标称值 7000 MB/s。

![图 9：最终性能对比。经过系统级调优，Solidigm D7-PS1030 的性能（14 GB/s）不仅完全达到其标称值，更显著超越社区平均水平（11.45 GB/s）。Utilization Ratio 为厂商公布结果除以规格标称顺序读最大值。](/media/blog/mlperf-storage-v2/fig-09.jpg)

下方平滑的性能曲线与之前的抖动图形成了鲜明对比，直观地证明了优化的巨大成功。

![图 10：优化后的稳定吞吐表现。与图 8 的剧烈抖动形成鲜明对比，这条平滑的性能曲线证明优化策略已彻底消除系统瓶颈。](/media/blog/mlperf-storage-v2/fig-10.jpg)

![图 11：Solidigm D5-P5336 在 MLPerf Storage v2.0 三个 AI 模型的训练基准测试结果。unet3d 中位数 7129.83 MB/s，在 AU > 90% 前提下稳定跑完 5 轮，超越标称值 7000 MB/s。](/media/blog/mlperf-storage-v2/fig-11.jpg)

## 6. 结论：Solidigm，构筑 AI 时代的高性能存储基石

本次 MLPerf Storage v2.0 的卓越测试成果，再次印证了 Solidigm SSD 的领先地位。像 D7-PS1030 和 D5-P5336 这样的产品，不仅拥有业界顶尖的硬件规格，更重要的是，它们能够在专业的系统级优化下，于严苛复杂的 AI 真实负载场景中，稳定发挥出极限性能，为 AI 基础架构提供坚如磐石的数据底座。

这个成功案例也充分说明，构建顶级的 AI 基础架构，需要顶尖的硬件、专业的工具和深厚的系统知识三者紧密结合。如果把 AI 训练比作一场顶级的一级方程式赛车（F1）比赛，Solidigm 的 SSD 就是那台拥有最强引擎和抓地力的顶级赛车，而 NewFW Technology 的 CWP 平台则是那位手握海量传感器数据的金牌工程师团队。这正是 Solidigm 致力于与像 NewFW Technology 这样的生态伙伴合作的价值所在——我们不仅提供卓越的产品，更协同伙伴的专业能力，帮助客户解决实际挑战，释放每一分性能潜力。未来，我们将继续携手合作伙伴，不断突破存储边界，为 AI 创新注入不竭动力。

如果您正在构建或升级您的 AI 基础架构，并寻求能够跟上算力增长步伐的高性能、高效率存储解决方案，Solidigm 企业级 SSD 无疑是您的明智之选。

MLPerf Storage v3.0 新增了备受关注的 KV Cache 测试项目。我们将于近期发布 MLPerf Storage v3.0 的测试数据，敬请期待。

探索企业级 SSD 解决方案，请访问 [Solidigm 官网数据中心产品页](https://www.solidigm.com/products/data-center.html)。

## 参考资料

1. ML Commons. *Introducing the MLPerf Storage Benchmark Suite*. 2024-06-23. [https://mlcommons.org/2023/06/introducing-the-mlperf-storage-benchmark-suite/](https://mlcommons.org/2023/06/introducing-the-mlperf-storage-benchmark-suite/)
2. Result verified by MLCommons Association. The MLPerf name and logo are registered and unregistered trademarks of MLCommons Association in the United States and other countries. All rights reserved. Unauthorized use strictly prohibited. See [www.mlcommons.org](https://www.mlcommons.org) for more information.
`;

export default markdown;
